import { ApiQuote, ApiJob, ApiInvoice } from './apiTypes'
import { logger } from './logger'
import { toneForStatus } from './phaseTone'
import { WORK_STAGE_LABELS } from '@/lib/labels'

export type WorkStage =
  | 'QUOTED'       // quote exists, not yet accepted
  | 'ACCEPTED'     // quote accepted, no job yet
  | 'SCHEDULED'    // job scheduled
  | 'IN_PROGRESS'  // job in progress
  | 'COMPLETED'    // job completed, no invoice
  | 'INVOICED'     // invoice exists, not yet paid
  | 'PAID'         // invoice paid
  | 'ARCHIVED'     // declined/cancelled/expired

/**
 * Computes the unified pipeline stage of a work item given its quote, job, and invoice.
 * Evaluates in order — first match wins.
 */
export function computeWorkStage(args: {
  quote?: ApiQuote
  job?: ApiJob
  invoice?: ApiInvoice
}): WorkStage {
  const { quote, job, invoice } = args

  // Invoice takes precedence when it's active (not cancelled / voided / written-off).
  // Terminal "dead" statuses (CANCELLED, VOIDED, WRITTEN_OFF) fall through so an
  // active job can still show its real stage (SCHEDULED / IN_PROGRESS) rather
  // than collapsing to ARCHIVED.
  if (
    invoice &&
    invoice.status !== 'CANCELLED' &&
    invoice.status !== 'VOIDED' &&
    invoice.status !== 'WRITTEN_OFF'
  ) {
    if (invoice.status === 'PAID') return 'PAID'
    // DRAFT, SENT, VIEWED, PARTIALLY_PAID, OVERDUE → all represent an active
    // invoice that hasn't been fully collected yet.
    if (
      invoice.status === 'DRAFT' ||
      invoice.status === 'SENT' ||
      invoice.status === 'VIEWED' ||
      invoice.status === 'PARTIALLY_PAID' ||
      invoice.status === 'OVERDUE'
    ) {
      return 'INVOICED'
    }
  }

  // Crew get no invoices; the server says the job's billing stage instead.
  if (!invoice && job?.billingStage) return job.billingStage

  // Job stages
  if (job) {
    if (job.status === 'COMPLETED') return 'COMPLETED'
    if (job.status === 'IN_PROGRESS') return 'IN_PROGRESS'
    // "Scheduled" means it has a date. A job created without one stays in
    // Approved ("Schedule job"), never "Scheduled · Scheduled".
    if (job.status === 'SCHEDULED') return job.scheduledStart ? 'SCHEDULED' : 'ACCEPTED'
    if (job.status === 'CANCELLED') return 'ARCHIVED'
  }

  // Terminal invoice statuses with no active job → archived.
  if (
    invoice?.status === 'CANCELLED' ||
    invoice?.status === 'VOIDED' ||
    invoice?.status === 'WRITTEN_OFF'
  ) {
    return 'ARCHIVED'
  }

  // Quote stages
  if (quote) {
    if (quote.status === 'ACCEPTED') return 'ACCEPTED'
    if (quote.status === 'DRAFT' || quote.status === 'SENT') return 'QUOTED'
    if (quote.status === 'DECLINED' || quote.status === 'EXPIRED') return 'ARCHIVED'
  }

  // Only log in dev — production mock console lingers
  if (import.meta.env.DEV && (quote?.status || job?.status || invoice?.status)) {
    logger.warn(
      '[computeWorkStage] Unhandled status combination',
      { quoteStatus: quote?.status, jobStatus: job?.status, invoiceStatus: invoice?.status }
    )
  }

  // Default fallback
  return 'QUOTED'
}

/**
 * Returns human-readable label for a work stage.
 */
/** Glossary label (lib/labels WORK_STAGE_LABELS): one of the 7 stages, ACCEPTED reads "Quoted · Approved". */
export function stageLabel(s: WorkStage): string {
  return WORK_STAGE_LABELS[s] ?? s
}

/**
 * Returns a Tailwind text color class for a work stage matching brand.
 * @deprecated Prefer `stageTone` + the pastel chip classes for the new
 *   design language. Kept so existing callers still compile.
 */
export function stageColor(s: WorkStage): string {
  const colors: Record<WorkStage, string> = {
    QUOTED:      'text-sky-200',
    ACCEPTED:    'text-lavender-200',
    SCHEDULED:   'text-butter-200',
    IN_PROGRESS: 'text-peach-200',
    COMPLETED:   'text-mint-200',
    INVOICED:    'text-blush-200',
    PAID:        'text-mint-200',
    ARCHIVED:    'text-white/40',
  }
  return colors[s]
}

export type WorkStageTone =
  | 'sky' | 'lavender' | 'butter' | 'peach' | 'mint' | 'blush' | 'neutral'

/**
 * Maps a work stage to one of the pastel design tones. Used by lane
 * headers and stage chips in the new language.
 *
 * Delegates to the phase rule in lib/phaseTone (one pastel per phase):
 *   QUOTED                           -> peach   (quote out)
 *   ACCEPTED, SCHEDULED, IN_PROGRESS -> sky     (production)
 *   COMPLETED, INVOICED              -> butter  (billing)
 *   PAID                             -> mint
 *   ARCHIVED                         -> neutral
 */
export function stageTone(s: WorkStage): WorkStageTone {
  return toneForStatus(s)
}

/**
 * Array of work stages in pipeline order.
 */
export const WORK_STAGE_ORDER: WorkStage[] = [
  'QUOTED',
  'ACCEPTED',
  'SCHEDULED',
  'IN_PROGRESS',
  'COMPLETED',
  'INVOICED',
  'PAID',
  'ARCHIVED',
]
